import { ApiError } from '../api/apiError';
import {
  evaluatePackageAdmission,
  getPackageAdmission,
  patchAdmissionFacts,
} from '../api/originatorApi';
import { buildAdmissionFactsRequest, mergeConfirmedFactCodes } from './admissionFacts';
import { ADMISSION_READINESS } from './labels';
import { waitForAdmissionEvaluation } from './pollAdmission';

function staleFactError(message, rowVersion, admission) {
  const error = new ApiError(message || 'Submission changed; review updated facts.', {
    status: 409,
    code: 'STALE_FACT_CONTEXT',
    payload: { row_version: rowVersion ?? null },
  });
  error.admission = admission ?? null;
  return error;
}

/**
 * Save Originator facts, then re-evaluate when the save clears readiness.
 * Callers must certify with the returned admission, not the pre-save hashes.
 */
export async function confirmAdmissionFacts({ packageId, reviewedRowVersion, facts }) {
  const reviewed = Number(reviewedRowVersion);
  const latest = await getPackageAdmission(packageId);
  if (Number(latest?.row_version) !== reviewed) {
    throw staleFactError(
      'Submission changed; review updated facts.',
      latest?.row_version,
      latest
    );
  }

  const body = buildAdmissionFactsRequest(reviewed, facts);
  let saved;
  try {
    saved = await patchAdmissionFacts(packageId, body);
  } catch (error) {
    if (error?.code === 'STALE_FACT_CONTEXT' || error?.status === 409) {
      try {
        error.admission = await getPackageAdmission(packageId);
      } catch {
        error.admission = null;
      }
    }
    throw error;
  }

  if (saved?.readiness_status !== ADMISSION_READINESS.NOT_EVALUATED) {
    const fetched = await getPackageAdmission(packageId);
    return { admission: mergeConfirmedFactCodes(fetched, saved), reevaluated: false };
  }

  const started = await evaluatePackageAdmission(packageId, {
    rowVersion: saved.row_version,
  });
  const immediate = started?.admission;
  const immediateStatus = immediate?.readiness_status || started?.readiness_status;
  if (immediate && immediateStatus && immediateStatus !== ADMISSION_READINESS.EVALUATING) {
    return { admission: mergeConfirmedFactCodes(immediate, saved), reevaluated: true };
  }
  if (immediateStatus && immediateStatus !== ADMISSION_READINESS.EVALUATING) {
    const fetched = await getPackageAdmission(packageId);
    return { admission: mergeConfirmedFactCodes(fetched, saved), reevaluated: true };
  }
  const settled = await waitForAdmissionEvaluation(() => getPackageAdmission(packageId));
  return { admission: mergeConfirmedFactCodes(settled, saved), reevaluated: true };
}
